import { HTMLTemplateElement, TypeError, isElementNode, isString, isTextNode, isUndefined, } from "@acng/frontend-bounty/types";
import { addClass, classList, cloneNode, createElement, hasClass, hasDataAttribute, isArray, parentElement, replace as replaceNode, setText, } from "@acng/frontend-bounty";
import { RESOURCE_EVENT, RESOURCE_TEXT, UUID_STARGAZER_LOADER } from "../model/resource.js";
import { rootNamespace } from "./root.js";
import { lookup } from "./lookup.js";
import { OPTIONAL, STRING, typeguard } from "@acng/frontend-bounty/assert";
/**
 * @typedef {import("../model/resource.js").ResourceElement} ResourceElement
 */
/**
 * @param {ResourceElement} resource
 * @param {ChildNode} childNode
 * @param {string[]} path
 * @param {Record<string, string> | undefined} replacements
 *
 * @returns {Promise<void>}
 * The path of the included (or hidden) resource to be used for {@link Dependencies}.
 */
export const replace = async (resource, childNode, path, replacements) => {
    try {
        const childResource = await lookup(rootNamespace, path);
        const template = cloneNode(childResource);
        if (replacements) {
            replaceChildNodes(template.content, replacements);
        }
        if (childResource[RESOURCE_TEXT]) {
            resource[RESOURCE_TEXT] = true;
        }
        if (childResource[RESOURCE_EVENT]) {
            resource[RESOURCE_EVENT] = true;
        }
        const relevantParentElement = parentElement(childNode) ?? resource;
        DEBUG: if (hasDataAttribute(resource, "debug")) {
            for (const token of classList(template)) {
                if (!hasClass(relevantParentElement, token)) {
                    console.warn("Adding missing classes to relevant parent element to satisfy css constraint.", {
                        resource,
                        relevantParentElement,
                        template,
                    });
                    break;
                }
            }
        }
        // Add the template classList to the parent element OR to the template element.
        addClass(relevantParentElement, ...classList(template));
        replaceNode(childNode, template.content);
    }
    catch (reason) {
        if (reason instanceof HTMLTemplateElement) {
            // The resource is hidden TODO HiddenResource
            return;
        }
        console.error(UUID_STARGAZER_LOADER, { reason, resource, childNode });
        // Add some verbose stuff to the resource because some devs do not read console output.
        // resource `path` not found, which is included in `resource`
        const errorElement = createElement("strong");
        Object.assign(errorElement.style, {
            background: "#000",
            border: "2px dashed #ff0",
            outline: "2px solid #000",
            color: "#ff0",
            padding: "5px 10px",
            "border-radius": "5px",
        });
        setText(errorElement, `🚧 ${childNode.textContent} 🚧`);
        replaceNode(childNode, errorElement);
    }
    //DEBUG: return {path: path.join("."), replacements};
};
/**
 * @param {ParentNode} parentNode
 * @param {Record<string, string | string[]>} replacements
 */
const replaceChildNodes = (parentNode, replacements) => {
    for (const childNode of parentNode.childNodes) {
        if (isTextNode(childNode)) {
            // Typescript thinks that nodeValue of Text might be null.
            ASSERT: typeguard("Text.nodeValue", childNode.nodeValue, STRING);
            const match = /^${([^}]*)}$/.exec(childNode.nodeValue);
            if (match) {
                const replacement = replacements[match[1]];
                if (isString(replacement)) {
                    setText(childNode, replacement);
                }
                else if (isArray(replacement)) {
                    // TODO need the "promises" here to add the lookup
                    throw Error("not implemented");
                }
                else if (!isUndefined(replacement)) {
                    throw TypeError();
                }
            }
        }
        else if (isElementNode(childNode)) {
            replaceAttributes(childNode, replacements);
            replaceChildNodes(childNode, replacements);
        }
    }
};
/**
 * @param {Element} element
 * @param {Record<string, string | string[]>} replacements
 */
const replaceAttributes = (element, replacements) => {
    for (const attr of element.attributes) {
        if (/^on:/.test(attr.name)) {
            let value = replacements[attr.value];
            DEBUG: if (!isString(value) || !isUndefined(value)) {
                console.error();
                value = attr.value;
            }
            attr.value = value;
        }
    }
};
